/**
 * VatWorkflow.jsx  —  VATICA AI  (AVALITH IT PTY LTD)
 *
 * VAT Return & Schedules tab inside ClientDashboard.
 *
 * Flow:
 *   Step 1 — Select which ERS documents to generate
 *   Step 2 — Upload the VATICA-extracted data Excel(s)
 *   Step 3 — Confirm taxpayer / signatory details
 *   Step 4 — Generate → calls Python backend → downloads ZIP
 *
 * Static ERS templates must be placed in:
 *   public/ers-templates/VAT_schedule_form--input_tab.xlsx
 *   public/ers-templates/VAT_schedule_form--output_tab.xlsx
 *   public/ers-templates/Document__1_.pdf          (VT.01)
 *
 * Backend endpoint: POST /api/vat/generate  (api_vat_endpoints.py)
 * All Excel reading and PDF filling happens server-side via openpyxl + pypdf.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { generateVatDocuments } from './api';

// ── Template paths (served from React public/ folder) ────────────────────────
const ERS_TEMPLATES = {
  input:     '/ers-templates/VAT_schedule_form--input_tab.xlsx',
  output:    '/ers-templates/VAT_schedule_form--output_tab.xlsx',
  vatReturn: '/ers-templates/Document__1_.pdf',
};

// ── Document type definitions ─────────────────────────────────────────────────
const DOC_TYPES = [
  {
    id:      'input_schedule',
    icon:    '📥',
    title:   'Input Schedule',
    sub:     'Purchases & expenses\n(ERS xlsx template)',
    dataKey: 'input',
  },
  {
    id:      'output_schedule',
    icon:    '📤',
    title:   'Output Schedule',
    sub:     'Sales & supplies\n(ERS xlsx template)',
    dataKey: 'output',
  },
  {
    id:      'vat_return',
    icon:    '📋',
    title:   'VAT Return (VT.01)',
    sub:     'Official ERS return form\nPDF — auto-totalled from schedules',
    dataKey: null,
  },
];

// ── Component ─────────────────────────────────────────────────────────────────

export default function VatWorkflow({ clientId, clientName, clientTin }) {
  // Step 1 — Document selection
  const [selectedDocs, setSelectedDocs] = useState(
    new Set(['input_schedule', 'output_schedule', 'vat_return'])
  );

  // Step 2 — Uploaded data Excel files
  const [uploadedFiles, setUploadedFiles] = useState({ input: null, output: null });
  const inputFileRef  = useRef(null);
  const outputFileRef = useRef(null);

  // Step 3 — Taxpayer / signatory info
  const [taxpayer, setTaxpayer] = useState({
    name:          clientName || '',
    tin:           clientTin  || '',
    telephone:     '',
    cellphone:     '',
    email:         '',
    designation:   '',
    signatoryName: '',
    periodFrom:    '',
    periodTo:      '',
  });

  // UI state
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');

  // ── Re-sync on client switch ───────────────────────────────────────────────
  // VatWorkflow now stays mounted across tab switches (see PersistentRoute in
  // ClientDashboard), so clientId/clientName/clientTin can change as props
  // without the component remounting. The useState() initial values above
  // only apply once, on first mount — without this effect, switching the VAT
  // client via ClientSelector would leave the form stuck on whichever client
  // was selected when VatWorkflow first rendered. We deliberately clear the
  // uploaded Excel files too, since those are tied to the previous client's
  // data and would silently get attributed to the new one.
  const prevClientIdRef = useRef(clientId);
  useEffect(() => {
    if (prevClientIdRef.current === clientId) return;
    prevClientIdRef.current = clientId;
    setTaxpayer(prev => ({ ...prev, name: clientName || '', tin: clientTin || '' }));
    setUploadedFiles({ input: null, output: null });
    setError('');
    setSuccess('');
  }, [clientId, clientName, clientTin]);

  // ── Helpers ─────────────────────────────────────────────────────────────────

  const toggleDoc = (id) => {
    setSelectedDocs(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleFileChange = useCallback((key) => (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadedFiles(prev => ({ ...prev, [key]: file }));
    setError('');
    setSuccess('');
  }, []);

  const updateTaxpayer = useCallback((field) => (e) => {
    setTaxpayer(prev => ({ ...prev, [field]: e.target.value }));
  }, []);

  // ── Validation ──────────────────────────────────────────────────────────────

  function validate() {
    if (selectedDocs.size === 0)
      return 'Select at least one document to generate.';
    if (selectedDocs.has('input_schedule') && !uploadedFiles.input)
      return 'Please upload a Purchases/Inputs Excel for the Input Schedule.';
    if (selectedDocs.has('output_schedule') && !uploadedFiles.output)
      return 'Please upload a Sales/Outputs Excel for the Output Schedule.';
    // Taxpayer details only required when generating the VAT Return form.
    // Excel schedules alone can be generated without any taxpayer information.
    if (selectedDocs.has('vat_return')) {
      if (!taxpayer.name)
        return 'Taxpayer name is required for the VAT Return form.';
      if (!taxpayer.tin)
        return 'TIN is required for the VAT Return form.';
      if (!taxpayer.periodFrom || !taxpayer.periodTo)
        return 'Tax period (From and To dates) are required for the VAT Return form.';
    }
    return null;
  }

  // ── Fetch ERS template as Blob ──────────────────────────────────────────────

  async function fetchTemplateBlob(path) {
    const resp = await fetch(path);
    if (!resp.ok) return null;   // missing template → skip silently
    // Nginx SPA catch-all returns index.html (text/html, status 200) for any
    // path that doesn't exist on disk. Without this check that HTML page would
    // be sent to the backend as the template → BadZipFile crash.
    const ct = resp.headers.get('content-type') || '';
    if (ct.includes('text/html')) return null;
    return resp.blob();
  }

  // ── Generate → POST to backend → download ZIP ───────────────────────────────

  async function handleGenerate() {
    const validationError = validate();
    if (validationError) { setError(validationError); return; }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const docTypes = [...selectedDocs];

      // Fetch ERS template blobs from public/ (in parallel)
      const [inputTemplateBlob, outputTemplateBlob, pdfTemplateBlob] = await Promise.all([
        selectedDocs.has('input_schedule')  ? fetchTemplateBlob(ERS_TEMPLATES.input)     : Promise.resolve(null),
        selectedDocs.has('output_schedule') ? fetchTemplateBlob(ERS_TEMPLATES.output)    : Promise.resolve(null),
        selectedDocs.has('vat_return')      ? fetchTemplateBlob(ERS_TEMPLATES.vatReturn) : Promise.resolve(null),
      ]);

      // Build multipart FormData
      const fd = new FormData();
      fd.append('doc_types',     JSON.stringify(docTypes));
      fd.append('taxpayer_name', taxpayer.name);
      fd.append('tin',           taxpayer.tin);
      fd.append('telephone',     taxpayer.telephone     || '');
      fd.append('cellphone',     taxpayer.cellphone     || '');
      fd.append('email',         taxpayer.email         || '');
      fd.append('period_from',   taxpayer.periodFrom);
      fd.append('period_to',     taxpayer.periodTo);
      fd.append('designation',   taxpayer.designation   || '');
      fd.append('full_name',     taxpayer.signatoryName || taxpayer.name);

      // User's VATICA-extracted Excel files
      if (uploadedFiles.input)  fd.append('input_excel',  uploadedFiles.input);
      if (uploadedFiles.output) fd.append('output_excel', uploadedFiles.output);

      // ERS template files (fetched from public/)
      if (inputTemplateBlob)  fd.append('input_template',  inputTemplateBlob,  'input_template.xlsx');
      if (outputTemplateBlob) fd.append('output_template', outputTemplateBlob, 'output_template.xlsx');
      if (pdfTemplateBlob)    fd.append('pdf_template',    pdfTemplateBlob,    'vt01_template.pdf');

      // POST to backend (routed through api.js so this carries the auth
      // token like every other endpoint, and a 401 triggers a clean
      // session-expired logout instead of a raw "Server error 401" message)
      const { files } = await generateVatDocuments(fd);

      if (!files || files.length === 0) {
        throw new Error('No files were returned by the server.');
      }

      // Helper: base64 string → Blob
      const b64ToBlob = (b64, mime) => {
        const binary = atob(b64);
        const bytes  = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        return new Blob([bytes], { type: mime });
      };

      // Trigger one download per file with a small delay between each
      files.forEach((file, idx) => {
        setTimeout(() => {
          const blob = b64ToBlob(file.data, file.mime);
          const url  = URL.createObjectURL(blob);
          const a    = document.createElement('a');
          a.href          = url;
          a.download      = file.filename;
          a.style.display = 'none';
          document.body.appendChild(a);
          a.click();
          setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 300);
        }, idx * 400); // stagger downloads so the browser doesn't block them
      });

      // Success message
      const fileList = files
        .map(f => ({
          input_schedule:  'Input Schedule',
          output_schedule: 'Output Schedule',
          vat_return:      'VT.01 Return',
        }[f.key] || f.key))
        .join(', ');

      const pdfNote = selectedDocs.has('vat_return') && !pdfTemplateBlob
        ? ' (VT.01 skipped — place Document__1_.pdf in public/ers-templates/)'
        : '';

      setSuccess(`✓ Downloaded ${files.length} file${files.length !== 1 ? 's' : ''}: ${fileList}.${pdfNote}`);

    } catch (err) {
      console.error('[VatWorkflow] generate error:', err);
      setError(err.message || 'An unexpected error occurred. Check the browser console.');
    } finally {
      setLoading(false);
    }
  }

  // ── Derived UI flags ────────────────────────────────────────────────────────

  const needsInputUpload  = selectedDocs.has('input_schedule');
  const needsOutputUpload = selectedDocs.has('output_schedule');
  // Step 3 (taxpayer details) is only shown and required when generating
  // the VAT Return form. Excel-only generation skips it entirely.
  const needsVatReturn    = selectedDocs.has('vat_return');
  // Generate step number shifts up when Step 3 is visible
  const generateStepNum   = needsVatReturn ? 4 : 3;

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="vat-workflow">

      {/* ── Step 1: Document selection ────────────────────────────────────── */}
      <div className="vat-step">
        <div className="vat-step-label">
          Step 1 — Select documents to generate
          <span className="vat-step-hint"> (select all that apply)</span>
        </div>
        <div className="doc-selector-grid">
          {DOC_TYPES.map(doc => {
            const selected = selectedDocs.has(doc.id);
            return (
              <div
                key={doc.id}
                className={`doc-card${selected ? ' doc-card--selected' : ''}`}
                onClick={() => toggleDoc(doc.id)}
              >
                <div className="doc-card-check">✓</div>
                <div className="doc-card-icon">{doc.icon}</div>
                <div className="doc-card-title">{doc.title}</div>
                <div className="doc-card-sub">{doc.sub}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Step 2: Upload data Excels ────────────────────────────────────── */}
      {(needsInputUpload || needsOutputUpload) && (
        <div className="vat-step">
          <div className="vat-step-label">
            Step 2 — Upload VATICA-extracted data
            <span className="vat-step-hint"> (Excel files from VATICA extractors)</span>
          </div>

          {needsInputUpload && (
            <div style={{ marginBottom: 10 }}>
              <div className="field-label" style={{ marginBottom: 4 }}>
                Purchases / Inputs Excel
              </div>
              <div className="file-upload-row">
                <input
                  ref={inputFileRef}
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={handleFileChange('input')}
                />
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => inputFileRef.current?.click()}
                >
                  {uploadedFiles.input ? 'Change file' : 'Choose file'}
                </button>
                <span className={`file-upload-name${uploadedFiles.input ? ' file-upload-name--set' : ''}`}>
                  {uploadedFiles.input ? uploadedFiles.input.name : 'No file chosen'}
                </span>
              </div>
            </div>
          )}

          {needsOutputUpload && (
            <div>
              <div className="field-label" style={{ marginBottom: 4 }}>
                Sales / Outputs Excel
              </div>
              <div className="file-upload-row">
                <input
                  ref={outputFileRef}
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={handleFileChange('output')}
                />
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => outputFileRef.current?.click()}
                >
                  {uploadedFiles.output ? 'Change file' : 'Choose file'}
                </button>
                <span className={`file-upload-name${uploadedFiles.output ? ' file-upload-name--set' : ''}`}>
                  {uploadedFiles.output ? uploadedFiles.output.name : 'No file chosen'}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Step 3: Taxpayer details — only shown when VAT Return selected ── */}
      {needsVatReturn && (
      <div className="vat-step">
        <div className="vat-step-label">
          Step 3 — Taxpayer &amp; period details
          <span className="vat-step-hint"> (required for the VAT Return form)</span>
        </div>

        <div className="taxpayer-grid">
          <div className="field-group">
            <label className="field-label">Name of Taxpayer *</label>
            <input
              className="field-input"
              value={taxpayer.name}
              onChange={updateTaxpayer('name')}
              placeholder="Registered business name"
            />
          </div>

          <div className="field-group">
            <label className="field-label">TIN *</label>
            <input
              className="field-input"
              value={taxpayer.tin}
              onChange={updateTaxpayer('tin')}
              placeholder="Taxpayer Identification Number"
            />
          </div>

          <div className="field-group">
            <label className="field-label">Tax Period From *</label>
            <input
              className="field-input"
              value={taxpayer.periodFrom}
              onChange={updateTaxpayer('periodFrom')}
              placeholder="DD/MM/YYYY"
            />
          </div>

          <div className="field-group">
            <label className="field-label">Tax Period To *</label>
            <input
              className="field-input"
              value={taxpayer.periodTo}
              onChange={updateTaxpayer('periodTo')}
              placeholder="DD/MM/YYYY"
            />
          </div>

          <div className="field-group">
            <label className="field-label">Telephone</label>
            <input
              className="field-input"
              value={taxpayer.telephone}
              onChange={updateTaxpayer('telephone')}
              placeholder="e.g. +268 2XXX XXXX"
            />
          </div>

          <div className="field-group">
            <label className="field-label">Cellphone</label>
            <input
              className="field-input"
              value={taxpayer.cellphone}
              onChange={updateTaxpayer('cellphone')}
              placeholder="e.g. +268 7XXX XXXX"
            />
          </div>

          <div className="field-group">
            <label className="field-label">Email Address</label>
            <input
              className="field-input"
              type="email"
              value={taxpayer.email}
              onChange={updateTaxpayer('email')}
              placeholder="accounting@company.co.sz"
            />
          </div>

          <div className="field-group">
            <label className="field-label">Signatory Full Name</label>
            <input
              className="field-input"
              value={taxpayer.signatoryName}
              onChange={updateTaxpayer('signatoryName')}
              placeholder="Person certifying the return"
            />
          </div>

          <div className="field-group">
            <label className="field-label">Designation</label>
            <input
              className="field-input"
              value={taxpayer.designation}
              onChange={updateTaxpayer('designation')}
              placeholder="e.g. Director, Accountant"
            />
          </div>
        </div>
      </div>
      )} {/* end needsVatReturn */}

      {/* ── Step 3 or 4: Generate ────────────────────────────────────────── */}
      <div className="vat-step">
        <div className="vat-step-label">Step {generateStepNum} — Generate &amp; download</div>
        <div className="vat-generate-row" style={{ justifyContent: 'center' }}>
          <button
            className="btn btn-primary"
            onClick={handleGenerate}
            disabled={loading || selectedDocs.size === 0}
          >
            {loading ? 'Generating…' : '⬇ Generate ERS Documents'}
          </button>
          {!loading && selectedDocs.size === 0 && (
            <span className="generate-hint">Select at least one document above.</span>
          )}
          {loading && (
            <span className="generate-hint">Processing on server — please wait…</span>
          )}
        </div>

        {error && (
          <div style={{
            marginTop: 12,
            padding: '10px 14px',
            background: '#fff0f0',
            border: '1px solid #f5c2c2',
            borderRadius: 6,
            color: '#b91c1c',
            fontSize: 13,
          }}>
            ⚠ {error}
          </div>
        )}

        {success && (
          <div style={{
            marginTop: 12,
            padding: '10px 14px',
            background: '#f0fff4',
            border: '1px solid #a7f3d0',
            borderRadius: 6,
            color: '#065f46',
            fontSize: 13,
          }}>
            {success}
          </div>
        )}
      </div>


    </div>
  );
}
